import React from 'react';
import { Settings } from 'lucide-react';

const selectChevronStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.75rem center',
  backgroundSize: '1rem',
};

const AnalysisSettingsGrid = ({
  modelName,
  onModelChange,
  isChangingModel,
  resizeMode,
  onResizeModeChange,
  compressionMode,
  onCompressionModeChange,
  useEbayRag,
  onToggleEbayRag,
  isChangingEbayRag,
  useNotesRag,
  onToggleNotesRag,
  isChangingNotesRag,
  assumeHiddenItems,
  onToggleAssumeHiddenItems,
  isChangingAssumeHiddenItems,
}) => {
  return (
    <div className="mb-4 sm:mb-8 bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <div className="flex items-center gap-2 min-w-0 text-slate-700">
          <Settings size={18} className="shrink-0" />
          <span className="font-medium text-sm sm:text-base whitespace-nowrap">Model:</span>
          <select
            value={modelName}
            onChange={(e) => onModelChange(e.target.value)}
            disabled={isChangingModel}
            className={`w-full min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none text-sm font-medium shadow-sm ${
              isChangingModel ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
            }`}
            style={selectChevronStyle}
          >
            <optgroup label="GPT-5 Series (Latest)">
              <option value="gpt-5.4">gpt-5.4</option>
              <option value="gpt-5.4-mini">gpt-5.4-mini</option>
              <option value="gpt-5.2">gpt-5.2</option>
              <option value="gpt-5.1">gpt-5.1</option>
              <option value="gpt-5">gpt-5</option>
            </optgroup>
            <optgroup label="Gemini">
              <option value="gemini-flash-latest">gemini-flash-latest</option>
              <option value="gemini-2.5-pro">gemini-2.5-pro</option>
              <option value="gemini-2.5-flash">gemini-2.5-flash</option>
              <option value="gemini-2.0-flash">gemini-2.0-flash</option>
            </optgroup>
            <optgroup label="Claude">
              <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
              <option value="claude-haiku-4-5">claude-haiku-4-5</option>
              <option value="claude-opus-4-6">claude-opus-4-6</option>
              <option value="claude-opus-4-7">claude-opus-4-7</option>
            </optgroup>
            <optgroup label="GPT-4 Series">
              <option value="gpt-4o">gpt-4o</option>
              <option value="gpt-4o-mini">gpt-4o-mini</option>
              <option value="gpt-4.1">gpt-4.1</option>
              <option value="gpt-4.1-mini">gpt-4.1-mini</option>
            </optgroup>
            <optgroup label="Reasoning Models">
              <option value="o1">o1</option>
              <option value="o3-mini">o3-mini</option>
              <option value="o3">o3</option>
              <option value="o4-mini">o4-mini</option>
            </optgroup>
          </select>
          {isChangingModel && <span className="text-slate-400 text-sm shrink-0">Updating...</span>}
        </div>

        <div className="flex items-center gap-2 min-w-0 text-slate-700">
          <span className="font-medium text-sm sm:text-base whitespace-nowrap">Resize:</span>
          <select
            value={resizeMode}
            onChange={(e) => onResizeModeChange(e.target.value)}
            className="w-full min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none cursor-pointer text-sm font-medium shadow-sm"
            style={selectChevronStyle}
          >
            <option value="original">Original</option>
            <option value="1600">Max 1600px</option>
            <option value="1280">Max 1280px</option>
            <option value="1024">Max 1024px</option>
            <option value="768">Max 768px</option>
            <option value="512">Max 512px</option>
            <option value="128">Max 128px</option>
          </select>
        </div>

        <div className="flex items-center gap-2 min-w-0 text-slate-700">
          <span className="font-medium text-sm sm:text-base whitespace-nowrap">Compression:</span>
          <select
            value={compressionMode}
            onChange={(e) => onCompressionModeChange(e.target.value)}
            className="w-full min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none cursor-pointer text-sm font-medium shadow-sm"
            style={selectChevronStyle}
          >
            <option value="none">None</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div className="flex items-center gap-2 min-w-0 text-slate-700">
          <span className="font-medium text-sm sm:text-base whitespace-nowrap">eBay RAG:</span>
          <button
            type="button"
            onClick={() => onToggleEbayRag(!useEbayRag)}
            disabled={isChangingEbayRag}
            className={`w-full inline-flex items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              useEbayRag
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-300 bg-white text-slate-600'
            } ${isChangingEbayRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
          >
            <span>{useEbayRag ? 'Enabled' : 'Disabled'}</span>
            <span className="text-xs">{isChangingEbayRag ? 'Updating...' : 'Toggle'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 min-w-0 text-slate-700">
          <span className="font-medium text-sm sm:text-base whitespace-nowrap">Notes RAG:</span>
          <button
            type="button"
            onClick={() => onToggleNotesRag(!useNotesRag)}
            disabled={isChangingNotesRag}
            className={`w-full inline-flex items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              useNotesRag
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-300 bg-white text-slate-600'
            } ${isChangingNotesRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
          >
            <span>{useNotesRag ? 'Enabled' : 'Disabled'}</span>
            <span className="text-xs">{isChangingNotesRag ? 'Updating...' : 'Toggle'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 min-w-0 text-slate-700">
          <span className="font-medium text-sm sm:text-base whitespace-nowrap">Hidden Items:</span>
          <button
            type="button"
            onClick={() => onToggleAssumeHiddenItems(!assumeHiddenItems)}
            disabled={isChangingAssumeHiddenItems}
            className={`w-full inline-flex items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              assumeHiddenItems
                ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700'
                : 'border-slate-300 bg-white text-slate-600'
            } ${isChangingAssumeHiddenItems ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
          >
            <span>{assumeHiddenItems ? 'Enabled' : 'Disabled'}</span>
            <span className="text-xs">{isChangingAssumeHiddenItems ? 'Updating...' : 'Toggle'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisSettingsGrid;
